import type { DocumentPosition } from '@/service/api'
import type { DocumentRecord, PnidSource } from '@/types/catalog'

/**
 * Resolve the `doc_type` a board card should render for a stored
 * {@link DocumentPosition}.
 *
 * The board persists a per-card position with a `doc_type` that can be stale or
 * generic, so the rendered type is reconciled against the loaded catalog
 * records:
 *
 * 1. FS-556 — a P&ID/PFD added to a custom (non-master) board can be stored as
 *    a generic `'document'` position, but its true type lives on the catalog
 *    record. Prefer that so the card resolves to `pnid`/`pfd` and the thumbnail
 *    path fetches the processed P&ID image (`/v1/pnid/<id>/image`) instead of
 *    falling through to the raw `/document/<id>/download` path that renders the
 *    diagram card blank.
 * 2. A non-`'graph'` position is otherwise taken at face value.
 * 3. A `'graph'` position is mapped back to its source P&ID/PFD type (via the
 *    resolved `pnidDocuments` map, the record's `properties.pnid` pointer, or a
 *    `'pnid'` default) so the card renders a diagram thumbnail rather than a
 *    generic graph icon.
 *
 * Pure function (no React/store access) so it is unit-testable in isolation.
 */
export function resolveBoardDocType(
  doc: DocumentPosition,
  documents: DocumentRecord[],
  pnidDocuments: Map<string, PnidSource>,
): string {
  const fullDoc = documents.find((d) => d.id === doc.document_id)

  // (1) FS-556: trust the authoritative catalog type for P&ID/PFD even when the
  // stored position type is a generic 'document'.
  if (fullDoc && (fullDoc.type === 'pnid' || fullDoc.type === 'pfd')) return fullDoc.type

  // (2) Non-graph positions are taken as-is.
  if (doc.doc_type !== 'graph') return doc.doc_type

  // (3) Graph positions resolve back to their source P&ID/PFD type.
  const pnidDoc = pnidDocuments.get(doc.document_id)
  if (pnidDoc) return pnidDoc.type
  if (fullDoc?.properties.pnid) {
    const sourceDoc = documents.find((d) => d.id === fullDoc.properties.pnid)
    if (sourceDoc) return sourceDoc.type
    // Source doc not in the local store yet — default to 'pnid' so a thumbnail
    // is attempted; once pnidDocuments loads the precise type resolves above.
    return 'pnid'
  }
  // Graph records are always derived from P&ID/PFD processing — default to
  // 'pnid' so the card renders a thumbnail instead of a graph icon.
  if (fullDoc?.type === 'graph') return 'pnid'
  return doc.doc_type
}
